// src/components/features/content/CalendarGrid.jsx
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import { 
  format, 
  isToday, 
  isSameMonth, 
  isWeekend,
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval,
} from 'date-fns';

// Calendar Day Cell Component
const DayCell = ({ 
  day, 
  events = [], 
  isCurrentMonth = true, 
  onClick, 
  onEventClick, 
  onEventAction, 
  cellHeight = 120,
  renderEvent,
}) => {
  const theme = useTheme();
  
  // Determine cell background color
  const getCellBgColor = () => {
    if (isToday(day)) {
      return alpha(theme.palette.primary.main, 0.05);
    } else if (!isCurrentMonth) {
      return alpha(theme.palette.action.disabledBackground, 0.1);
    } else if (isWeekend(day)) {
      return alpha(theme.palette.action.hover, 0.05);
    }
    return theme.palette.background.paper;
  };
  
  // Determine cell border color
  const getCellBorderColor = () => {
    if (isToday(day)) {
      return alpha(theme.palette.primary.main, 0.3);
    }
    return 'transparent';
  };
  
  return (
    <Paper
      sx={{
        height: '100%',
        minHeight: cellHeight,
        p: 1,
        bgcolor: getCellBgColor(),
        border: '1px solid',
        borderColor: getCellBorderColor(),
        opacity: isCurrentMonth ? 1 : 0.7,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: isCurrentMonth ? 1 : 0,
        },
      }}
      onClick={() => onClick(day)}
    >
      <Typography 
        variant="caption" 
        sx={{ 
          fontWeight: isToday(day) ? 'bold' : 'normal',
          color: isToday(day) ? 'primary.main' : isCurrentMonth ? 'text.primary' : 'text.secondary',
          mb: 0.5
        }}
      >
        {format(day, 'd')}
      </Typography>
      
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {events.map((event, index) => (
          <Box key={index}>
            {renderEvent ? (
              renderEvent(event, () => onEventClick(event), (action) => onEventAction(action, event))
            ) : (
              <Box 
                sx={{ 
                  p: 1, 
                  mb: 1, 
                  borderRadius: 1, 
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick(event);
                }}
              >
                {event.title}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

/**
 * A reusable calendar grid component
 * 
 * @param {Object} props Component props
 * @param {Date} props.date Current displayed date
 * @param {Object} props.events Map of events by date
 * @param {Function} props.onDayClick Handler for day cell clicks
 * @param {Function} props.onEventClick Handler for event clicks
 * @param {Function} props.onEventAction Handler for event actions
 * @param {Function} props.renderEvent Custom renderer for events
 * @param {Number} props.cellHeight Height for day cells
 */
const CalendarGrid = ({
  date = new Date(),
  events = {},
  onDayClick,
  onEventClick,
  onEventAction,
  renderEvent,
  cellHeight = 120,
}) => {
  const theme = useTheme();
  
  // Calculate calendar parameters
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);
  
  // Generate all days to display
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Group events by date
  const getEventsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return events[dateStr] || [];
  };
  
  // Week day headers
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <Grid container spacing={1}>
      {/* Week day headers */}
      {weekDays.map((day, index) => (
        <Grid item xs={12/7} key={`header-${index}`}>
          <Typography 
            variant="caption" 
            align="center" 
            sx={{ 
              display: 'block', 
              fontWeight: 'medium',
              py: 0.5,
              color: index === 0 || index === 6 ? 'text.secondary' : 'text.primary',
            }}
          >
            {day}
          </Typography>
        </Grid>
      ))}
      
      {/* Calendar days */}
      {days.map((day, index) => (
        <Grid item xs={12/7} key={`day-${index}`}>
          <DayCell 
            day={day}
            events={getEventsForDay(day)}
            isCurrentMonth={isSameMonth(day, date)}
            onClick={onDayClick}
            onEventClick={onEventClick}
            onEventAction={onEventAction}
            renderEvent={renderEvent}
            cellHeight={cellHeight}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default CalendarGrid;